/*
 * 真实登录态：从米羊账号那边认出「这是谁、他有多少米粒」。
 *
 * 背景：这个工具不自建账号。用户在 miyang.cn 登录时，登录页把访问令牌写成
 *   cookie `miyang_at`，**domain 是 `.miyang.cn`**（见主站 templates/login.html），
 *   于是浏览器访问 lab.miyang.cn 时会带上它，而且它不是 HttpOnly，页面读得到。
 *   在这之前，这一页的登录态一直由演示条假扮（DemoBar 的 ?as=），
 *   于是一个真登录过的用户打开工具，看到的仍然是「登录」按钮。
 *
 * 设计意图：cookie 只当**令牌的载体**，不当判据。判据是网关的回答——
 *   带上 `Authorization: Bearer` 调一次 `/gateway/tools/quota`，
 *   它回 `logged_in` 与 `balance_points`。理由是 cookie 只能证明「浏览器上有一串字符」，
 *   证明不了它没过期、没被撤销、账号没被封。余额更是只有后端知道。
 *
 * 关键约束：
 *   1. **同源**。请求写成根路径 `/gateway/tools/quota`，由 lab.miyang.cn 的 nginx
 *      转给网关进程（8001）。不要改成 `https://miyang.cn/...`：那是跨源，
 *      带 Authorization 头会先来一次预检，等于给每次开页加一个来回和一处 CORS 配置。
 *   2. **只用于切 UI**。这里读到的余额是展示值，扣费与鉴权一律以处理请求的响应为准。
 *      令牌随时可能在两次请求之间失效，前端不该替后端下结论。
 *   3. 没有 cookie 就不发请求。未登录的访客占大多数，不该为了确认「他确实没登录」
 *      而给网关加一次请求。
 *   4. 演示条仍然说了算（见 isDemoPinned）：截图脚本靠 ?state= / ?as= 定身份，
 *      真实登录态若在它们之上再改一次，脚本拍出来的东西就不再稳定。
 */

/** 主站登录页写下的访问令牌 cookie，domain=.miyang.cn，非 HttpOnly。 */
const TOKEN_COOKIE = 'miyang_at'

export type Account = { balance: number }

function readToken(): string | null {
  const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${TOKEN_COOKIE}=([^;]*)`))
  if (!m) return null
  try {
    return decodeURIComponent(m[1]) || null
  } catch {
    // cookie 被别处写坏时不该让整页白屏，当作没登录即可。
    return null
  }
}

/** URL 上钉死了身份（?state= 或 ?as=），此时不去问真实登录态。 */
export function isDemoPinned(): boolean {
  const q = new URLSearchParams(window.location.search)
  return q.has('state') || q.has('as')
}

/**
 * 问一次网关：这个令牌还算数吗，余额多少。
 *
 * 未登录、令牌失效、网关不可用，一律返回 null——调用方按访客处理。
 * 不区分这三种情况是有意的：对页面来说它们的下一步都一样（显示登录入口），
 * 分开处理只会多出两条没人走的分支。
 */
export async function fetchAccount(signal?: AbortSignal): Promise<Account | null> {
  const token = readToken()
  if (!token) return null
  try {
    const resp = await fetch('/gateway/tools/quota', {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    })
    if (!resp.ok) return null
    const data = await resp.json()
    if (!data?.logged_in) return null
    return { balance: Number(data.balance_points) || 0 }
  } catch {
    return null
  }
}

/**
 * 换票时要带的鉴权头。
 *
 * 只有广州网关那一跳需要它（鉴权、余额、预扣都在那侧）。首尔那台不看令牌，
 * 只认广州签的票据——把用户令牌发到境外那台机器上没有必要，也多一处可泄漏点。
 * 没登录就返回空对象，此时网关一张票也不签（registration_required）：
 * 深度合成要先做真实身份信息认证，访客那一档已经撤了。
 */
export function authHeaders(): Record<string, string> {
  const token = readToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

/** 去主站登录，登完回到用户当前所在的这一页（带着他原来的查询参数）。 */
export function loginUrl(): string {
  return `https://miyang.cn/login?next=${encodeURIComponent(window.location.href)}`
}

/**
 * 去主站注册。同样带 next= 回到这一页：注册这一步是被我们打断的，
 * 办完得把人送回他原来站的地方，不能扔在主站首页让他自己找路回来。
 */
export function registerUrl(): string {
  return `https://miyang.cn/register?next=${encodeURIComponent(window.location.href)}`
}

/**
 * 有没有令牌 cookie。用来决定首屏那一格先留白还是先画「登录」：
 * 已登录的人若先看见「登录」再跳成余额，会以为自己掉线了。
 */
export function hasToken(): boolean {
  return readToken() !== null
}
